import { useEffect, useState } from "react"
import Modal from "Components/molecule/modal"
import ActionButton from "Components/atoms/buttons/action-button"
import TextInput from "Components/molecule/inputs/text-input"
import SearchSelect from "Components/molecule/inputs/search-select"
import TextareaInput from "Components/molecule/inputs/textarea-inpuit"
import { ExpenseBudget, ExpenseBudgetPayload, ExpenseBudgetSearchFields, employeeName } from "models/expense-budget"
import { departmentTreeOptions, fromPercent, toPercent } from "utils/expense-budget"
import FiscalCalendar from "utils/fiscal-calendar"

interface BudgetFormModalProps {
    isOpen: boolean
    budget?: ExpenseBudget | null
    searchFields: ExpenseBudgetSearchFields | null
    isSaving: boolean
    onClose: () => void
    onSave: (payload: ExpenseBudgetPayload) => Promise<void>
}

interface BudgetFormValues {
    departmentId: string
    fiscalYearId: string
    ownerId: string
    approvedAmount: string
    inflationPercent: string
    taxPercent: string
    alertPercentThreshold: string
    alertAmountThreshold: string
    notes: string
}

const toFormValues = (budget?: ExpenseBudget | null): BudgetFormValues => ({
    departmentId: budget?.departmentId ?? "",
    fiscalYearId: String(budget?.fiscalYearId ?? FiscalCalendar.currentFiscalYear()),
    ownerId: budget?.ownerId ?? "",
    approvedAmount: budget?.approvedAmount ?? "0",
    inflationPercent: String(toPercent(budget?.inflationRate ?? 0.05)),
    taxPercent: String(toPercent(budget?.defaultTaxRate ?? 0.07)),
    alertPercentThreshold: budget?.alertPercentThreshold ?? "10",
    alertAmountThreshold: budget?.alertAmountThreshold ?? "100",
    notes: budget?.notes ?? "",
})

const toPayload = (values: BudgetFormValues, isNew: boolean): ExpenseBudgetPayload => ({
    ...(isNew && { departmentId: values.departmentId, fiscalYearId: Number(values.fiscalYearId) }),
    ownerId: values.ownerId,
    approvedAmount: Number(values.approvedAmount),
    inflationRate: fromPercent(Number(values.inflationPercent)),
    defaultTaxRate: fromPercent(Number(values.taxPercent)),
    alertPercentThreshold: Number(values.alertPercentThreshold),
    alertAmountThreshold: Number(values.alertAmountThreshold),
    notes: values.notes || undefined,
})

const BudgetFormModal = ({ isOpen, budget, searchFields, isSaving, onClose, onSave }: BudgetFormModalProps) => {
    const isNew = !budget
    const [values, setValues] = useState<BudgetFormValues>(toFormValues(budget))

    useEffect(() => { if (isOpen) setValues(toFormValues(budget)) }, [isOpen, budget])

    const handleChange = (key: keyof BudgetFormValues, value: string) => setValues(prev => ({ ...prev, [key]: value }))

    const departmentOptions = departmentTreeOptions(searchFields?.departments ?? [])
    const yearOptions = (searchFields?.fiscalYears ?? []).map(y => ({ value: String(y.id), label: `FY${y.id}` }))
    const employeeOptions = (searchFields?.employees ?? []).map(e => ({ value: e.id, label: employeeName(e) }))
    const canSave = values.ownerId && (!isNew || (values.departmentId && values.fiscalYearId))

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isNew ? "New Expense Budget" : "Edit Budget"}>
            <div className="d-flex flex-column gap-2">
                {isNew && <>
                    <SearchSelect label="Department" options={departmentOptions}
                        value={values.departmentId}
                        onChange={option => handleChange("departmentId", option?.value ?? "")} />
                    <SearchSelect label="Fiscal Year" options={yearOptions}
                        value={values.fiscalYearId}
                        onChange={option => handleChange("fiscalYearId", option?.value ?? "")} />
                </>}
                <SearchSelect label="Budget Owner" options={employeeOptions}
                    value={values.ownerId}
                    onChange={option => handleChange("ownerId", option?.value ?? "")} />
                <TextInput label="Approved Amount (CAD)" type="number" min={0} value={values.approvedAmount}
                    onChange={e => handleChange("approvedAmount", e.target.value)} />
                <div className="d-flex gap-2">
                    <TextInput label="Inflation %" type="number" min={0} value={values.inflationPercent}
                        onChange={e => handleChange("inflationPercent", e.target.value)} />
                    <TextInput label="Default Tax %" type="number" min={0} value={values.taxPercent}
                        onChange={e => handleChange("taxPercent", e.target.value)} />
                </div>
                <div className="d-flex gap-2">
                    <TextInput label="Alert when over plan by %" type="number" min={0} value={values.alertPercentThreshold}
                        onChange={e => handleChange("alertPercentThreshold", e.target.value)} />
                    <TextInput label="…and by at least $" type="number" min={0} value={values.alertAmountThreshold}
                        onChange={e => handleChange("alertAmountThreshold", e.target.value)} />
                </div>
                <TextareaInput label="Notes" max={2000} value={values.notes} onChange={e => handleChange("notes", e.target.value)} />
                <div className="d-flex justify-content-end gap-2">
                    <ActionButton onClick={onClose}>Cancel</ActionButton>
                    <ActionButton disabled={!canSave || isSaving} onClick={() => onSave(toPayload(values, isNew))}>
                        {isSaving ? "Saving..." : "Save"}
                    </ActionButton>
                </div>
            </div>
        </Modal>
    )
}

export default BudgetFormModal
